import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, of, switchMap } from 'rxjs';

import {
  type CanvasElement,
  type CanvasElementId,
  type FormContainerVariantType,
} from '@blaze-runtime/engine-domain';
import { FIELD_INFO_TYPE_COMMIT } from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementPropertiesWithData,
  ELEMENT_PATH_FG_WN,
  ELEMENT_PATH_WF,
  type ElementSizingMode,
  getActiveBinding,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSection,
  type PropertiesConfigSubsection,
} from '@blaze-shared/elements-domain';
import { type FormActionType } from '@blaze-shared/workflow-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { getElementById } from '../../+state/elements';
import {
  formColumnsRegenerateRequest,
  showEditFiltersDialog,
} from '../../+state/elements/elements.actions';
import { hasWrapperRefs } from '../../canvas-elements/element-model/element-props-guards';
import { WorkflowUtilsService } from '../../services/workflow-utils.service';
import {
  propsConfigHelper as epc,
  mapSizingModesToOptions,
} from '../../utils/elements-props-config.helpers';

import { formFieldsField } from './form-container.element.utils';
import { DEFAULT_SUCCESS_MESSAGE, type FormContainerPropsModel } from './form-container.model';

const FORM_CONTAINER_MIN_WIDTH = 150;
const FORM_CONTAINER_MAX_WIDTH = 900;
const FORM_CONTAINER_WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];

const CHILD_MIN_SIZE = 40;
const CHILD_MAX_WIDTH = 900;
const CHILD_MAX_HEIGHT = 150;

const CHILD_WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];

export function getPropsConfig(formVariant: FormContainerVariantType): PropertiesConfig {
  const dataSourceSubsection = getDataSourceSubsection(formVariant);

  return epc.initConfig(formVariant, [
    epc.section('content', 'Content', [
      dataSourceSubsection,

      epc.subsections.subsection(
        'content.data',
        'Fields',
        [
          epc.utils.conditionalField(
            formFieldsField('content.model', 'data.?key:value.dataSource'),
            'content.model',
            (_, model: FormContainerPropsModel) => isGeneratedPrompt(model),
          ),
          epc.utils.conditionalField(
            epc.fields.listFieldsField('content.model', 'data.?key:value.dataSource', true),
            'content.model',
            (_, model: FormContainerPropsModel) => !isGeneratedPrompt(model),
          ),
        ],
        { hideLabel: true },
      ),

      epc.subsections.subsection(
        'content',
        '',
        [
          epc.fields.subConfigField('content.children', 'Format All Fields', {
            dialogId: 'GENERIC:FORM_FIELDS_FORMAT',
            dialogTitle: 'All Fields',
            configFields: getAllFieldsFloatingPanelConfig(formVariant),
            initSectionIdx: 0,
          }),
        ],
        { hideLabel: true, labelWidth: '70%' },
      ),

      epc.subsections.subsection(
        'content.options',
        'Options',
        [
          epc.fields.sliderField('content.options.columnsCount', 'Columns', {
            units: [{ min: 1, max: 4 }],
            emitOnThumbRelease: true,
            actionOnChange: (elementId: CanvasElementId, value: number) =>
              formColumnsRegenerateRequest({ elementId, value }),
          }),
          ...(formVariant !== 'formContainer'
            ? []
            : [
                epc.fields.toggleField(
                  'content.options.markRequiredFields',
                  'Mark required fields with *',
                  { renderType: 'check' },
                ),
                epc.fields.toggleField('content.options.submitOnEnter', 'Submit on Enter', {
                  renderType: 'check',
                }),
                epc.fields.toggleField('content.options.changeOnBlur', 'Update on blur', {
                  renderType: 'check',
                }),
                epc.fields.toggleField(
                  'content.options.requiredDisableSubmit',
                  'Empty required fields disable submit',
                  {
                    renderType: 'check',
                  },
                ),
                epc.fields.toggleField(
                  'content.options.scrollToError',
                  'Scroll to element on failure',
                  {
                    renderType: 'check',
                  },
                ),
              ]),
        ],
        {
          lockWhen: (props: any) =>
            getActiveBinding((props as CanvasElementPropertiesWithData)?.data, 'value')
              ? undefined
              : 'The Value of the element is bound',
        },
      ),

      epc.subsections.subsection(
        'content.postAction',
        'Success Message',
        [
          epc.fields.textAreaField(
            `${ELEMENT_PATH_WF}nodes.?%hasResultMessages.successMessage`,
            'Message',
            { hideLabel: true, valueUntilResolved: DEFAULT_SUCCESS_MESSAGE },
          ),
          getActionTypeConfig('insert'),
          getActionTypeConfig('update'),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromApi(
              `${ELEMENT_PATH_WF}nodes.?%isNavigateNode.target`,
              'Go to page',
              'LIST_PAGES',
              { allowNone: false, showSelection: true },
            ),
            'content.postAction.actionType',
            (model) => model !== 'Page',
          ),
        ],
        {
          hideWhen: (_, __, model: FormContainerPropsModel) => {
            if (
              formVariant === 'formDetails' ||
              isGeneratedPrompt(model) ||
              model.content.origin === 'form-group'
            ) {
              return true;
            }
            const tempEl = { props: model } as CanvasElement;
            if (!hasWrapperRefs(tempEl)) {
              return true;
            }
            return !tempEl.props.wrapper.refs[FIELD_INFO_TYPE_COMMIT];
          },
        },
      ),

      epc.subsections.visibilitySubsection('', {
        hideWhen: (_, __, model: FormContainerPropsModel) => isGeneratedPrompt(model),
      }),

      buildActionsSubsection(formVariant),

      epc.subsections.subsection(
        '',
        '',
        [
          ...epc.fields.emptyStateFields({
            messagePath: 'content.noDataMessage',
          }),
        ],
        {
          hideLabel: true,
          hideWhen: () => formVariant !== 'formDetails',
        },
      ),

      epc.subsections.titleContentSubsection('content'),

      epc.subsections.subsection('content', 'Size', [
        epc.root.elementWidthField(
          { min: FORM_CONTAINER_MIN_WIDTH, max: FORM_CONTAINER_MAX_WIDTH, unit: 'px', step: 1 },
          FORM_CONTAINER_WIDTH_OPTIONS,
        ),
      ]),
    ]),

    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.container', 'Padding & Margin', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.container', 'Appearance', [
          epc.fields.colorField('style.container.fill', 'Fill', { defaultValue: undefined }),
          epc.fields.colorField('style.container.outline', 'Outline', {
            allowGradient: false,
            defaultValue: undefined,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.container.outlineWidth', 'Width', [
            { min: 0, max: 10, unit: 'px' },
          ]),
          epc.fields.radiusField('style.container.radius'),
          ...epc.fields.visualEffectField('style.container.visualEffect'),
        ]),

        epc.subsections.subsection(
          'style',
          '',
          [
            epc.fields.subConfigField('style.children', 'Style All Fields', {
              dialogId: 'GENERIC:FORM_FIELDS_STYLE',
              dialogTitle: 'All Fields',
              configFields: getAllFieldsFloatingPanelConfig(formVariant),
              initSectionIdx: 1,
            }),
          ],
          { hideLabel: true, labelWidth: '70%' },
        ),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.container.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

function buildActionsSubsection(formVariant: FormContainerVariantType): PropertiesConfigSubsection {
  const bottomActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.containerItemActionsField('content.formActions', 'Actions', {
      generateActionsIds: true,
      simpleButtonConfig: true,
      actionPaneTitle: 'Action: Bottom',
      actionInitialValues: { label: 'Custom Action' },
    }),
    ...epc.utils.conditionalFields(
      [
        epc.utils.separator('Layout'),
        epc.fields.radiosField(
          'content.options.actionsLayout',
          '',
          [
            { value: 'stacked', label: 'Stacked' },
            { value: 'sideBySide', label: 'Side by Side' },
          ],
          { hideLabel: true, leftIndent: true },
        ),
      ],
      'content.formActions',
      (formActions: FormContainerPropsModel['content']['formActions']) =>
        (formActions?.length ?? 0) < 2,
    ),
  ];

  const headerActionsGroup: PropertiesConfigItemDefinition[] = [
    epc.fields.containerItemActionsField('content.headerActions', 'Actions', {
      generateActionsIds: true,
      simpleButtonConfig: true,
      actionPaneTitle: 'Action: Header',
      actionEventType: 'action',
      skipVisibility: false,
      actionInitialValues: { label: 'Custom Action', icon: 'flash' },
    }),
  ];

  return epc.subsections.subsection(
    'content',
    'Actions',
    [
      epc.fields.radiosField(
        'content.actionsView',
        '',
        [
          {
            value: 'bottom',
            label: 'Bottom',
            badge: (props: FormContainerPropsModel) =>
              props.content.formActions?.length
                ? props.content.formActions.length.toString()
                : undefined,
          },
          {
            value: 'header',
            label: 'Header',
            badge: (props: FormContainerPropsModel) =>
              props.content.headerActions?.length
                ? props.content.headerActions.length.toString()
                : undefined,
          },
        ],
        { hideLabel: true, leftIndent: true },
      ),
      ...epc.utils.conditionalFields(
        bottomActionsGroup,
        'content.actionsView',
        (actionsView: FormContainerPropsModel['content']['actionsView']) =>
          actionsView === 'header',
      ),
      ...epc.utils.conditionalFields(
        headerActionsGroup,
        'content.actionsView',
        (actionsView: FormContainerPropsModel['content']['actionsView']) =>
          actionsView !== 'header',
      ),
    ],
    {
      hideLabel: true,
      hideWhen: () => formVariant !== 'formDetails',
      lockWhen: (model: FormContainerPropsModel) =>
        !getValueByPath(model, 'data.?key:value.dataSource')
          ? 'The Value of the element is not bound'
          : undefined,
    },
  );
}

function getAllFieldsFloatingPanelConfig(
  formVariant: FormContainerVariantType,
): PropertiesConfigSection[] {
  const content = [
    epc.subsections.subsection('content.children.size', 'Size', [
      epc.fields.sizeField({
        path: 'content.children.size.width',
        label: 'Width',
        units: [{ min: CHILD_MIN_SIZE, max: CHILD_MAX_WIDTH, unit: 'px', step: 1 }],
        sizingOptions: mapSizingModesToOptions(CHILD_WIDTH_OPTIONS),
      }),
      epc.fields.sizeField({
        path: 'content.children.size.height',
        label: 'Height',
        units: [{ min: CHILD_MIN_SIZE, max: CHILD_MAX_HEIGHT, unit: 'px', step: 1 }],
      }),
    ]),
    epc.subsections.triggerSubsection({
      label: 'Overwrite Custom Settings',
      action: 'APPLY_PARENT_FORMAT',
    }),
  ];
  const style = [
    epc.subsections.subsection(
      'style.children.titleLabel',
      'Label',
      [
        epc.fields.colorField('style.children.titleLabel.color', 'Color', {
          allowGradient: false,
          allowNone: false,
          defaultValue: undefined,
        }),
        epc.fields.titlePositionField('content.children.label.position'),
        epc.fields.horizontalAlignField('content.children.label.align', {
          noCenter: true,
        }),
        ...epc.utils.conditionalFields(
          formVariant === 'formContainer'
            ? [
                epc.fields.sliderField('content.children.label.width', 'Width', [
                  { min: 0, max: 80, unit: '%', step: 1 },
                ]),
              ]
            : [
                epc.fields.radiosField(
                  'content.children.label.widthMode',
                  'Width',
                  [
                    { value: 'fixed', label: 'Fixed' },
                    { value: 'label', label: 'Auto' },
                  ],
                  { renderType: 'text', stretched: true },
                ),
                epc.utils.conditionalField(
                  epc.fields.sliderField('content.children.label.width', 'Amount', [
                    { min: 0, max: 80, unit: '%', step: 1 },
                  ]),
                  'content.children.label.widthMode',
                  (mode) => mode === 'label',
                ),
              ],
          'content.children.label.position',
          (labelPosition) => labelPosition === 'top',
        ),
        // TBA in the next sprint
        // epc.fields.selectFieldFromFormFonts('style.children.titleLabel.fontFamily'),
        // epc.fields.fontStyleField('style.children.titleLabel.style', 'Style', false),
        // epc.fields.sliderField('style.children.titleLabel.fontSize', 'Size', [{ min: 8, max: 72, unit: 'px' }]),
      ],
      { classes: ['!border-t-0'] },
    ),
    epc.subsections.subsection('style.children.fieldLabel', 'Text Field', [
      epc.fields.colorField('style.children.fieldLabel.color', 'Color', {
        allowGradient: false,
        allowNone: false,
        defaultValue: undefined,
      }),
      epc.fields.selectFieldFromFormFonts('style.children.fieldLabel.fontFamily'),
      epc.fields.fontStyleField('style.children.fieldLabel.style', 'Style'),
      epc.fields.sliderField('style.children.fieldLabel.fontSize', 'Size', [
        { min: 8, max: 72, unit: 'px' },
      ]),
    ]),
    epc.subsections.subsection('style.children.fieldBox', 'Field or Symbol Box', [
      epc.fields.colorField('style.children.fieldBox.accent', 'Accent', {
        allowGradient: false,
        allowNone: false,
        defaultValue: undefined,
      }),
      epc.fields.colorField('style.children.fieldBox.fill', 'Fill', {
        defaultValue: undefined,
      }),
      epc.fields.colorField('style.children.fieldBox.outline', 'Outline', {
        allowGradient: false,
        allowSolid: true,
        defaultValue: undefined,
      }),
      epc.fields.sliderField('style.children.fieldBox.outlineWidth', 'Width', [
        { min: 0, max: 25, unit: 'px' },
      ]),
      epc.fields.radiusField('style.children.fieldBox.radius', 'Radius'),
    ]),
    epc.subsections.triggerSubsection({
      label: 'Overwrite Custom Settings',
      action: 'APPLY_PARENT_STYLE',
    }),
  ];

  return [epc.section('content', 'Content', content), epc.section('style', 'Style', style)];
}

function getActionTypeConfig(
  actionType: 'insert' | 'update',
): PropertiesConfigItemDefinition<'radios'> {
  const options = [
    { value: 'Page', label: 'Open Page' },
    { value: 'None', label: 'None' },
  ];
  if (actionType === 'insert') {
    options.unshift({ value: 'Clear', label: 'Clear Form' });
  }
  return epc.utils.conditionalField(
    epc.fields.radiosField('content.postAction.actionType', 'After action', options, {
      wrapped: true,
    }),
    'content.action',
    (model) => model !== actionType,
  );
}

function getDataSourceSubsection(
  formVariant: FormContainerVariantType,
): PropertiesConfigSubsection {
  if (formVariant === 'formContainer') {
    return epc.subsections.subsection(
      'data.?key:value.enabled',
      'Form Action',
      [
        epc.utils.disabledField(
          epc.fields.selectFieldFromFormActions('content.action', 'Action', {
            layoutMode: 'datasource-selection',
          }),
          (model) => {
            const ds = getValueByPath(model, 'data.?key:value.dataSource');
            if (!ds) {
              return undefined;
            }
            const content: FormContainerPropsModel['content'] | undefined | null = getValueByPath(
              model,
              'content',
            );
            if (
              (content?.origin === 'drag' && content?.action === 'insert') ||
              content?.action === 'update'
            ) {
              return undefined;
            }

            return 'The action cannot be changed after initial data source selection';
          },
        ),
        epc.utils.conditionalField(
          epc.fields.selectDataSourceField(
            { key: 'value', label: 'Data source', sources: 'list', bindings: [] },
            'form-action',
            '',
            { hideAiButton: true },
          ),
          'content.action',
          (model: FormActionType) => !model,
        ),
        epc.utils.conditionalField(
          epc.fields.textInput('', 'Row', {
            displayValue: (elementId: CanvasElementId) => {
              const store = inject(Store);
              const workflowUtils = inject(WorkflowUtilsService);
              return store.select(getElementById(elementId)).pipe(
                switchMap((element) => {
                  const props = (element as any)?.props as FormContainerPropsModel | undefined;
                  if (!props) {
                    return of('Select Row');
                  }
                  if (props.content?.origin === 'generated') {
                    return of('Selected at Runtime');
                  }

                  const formDataSource = (props as CanvasElementPropertiesWithData)?.data?.find(
                    (b) => b.enabled !== undefined && b.key === 'value',
                  )?.dataSource;
                  if (!formDataSource?.id) {
                    return of('Select Row');
                  }
                  if (props.content?.origin === 'form-group') {
                    return of('Selected');
                  }

                  return workflowUtils.areAnyFiltersApplied(formDataSource.id).pipe(
                    map((hasFilters) => {
                      if (hasFilters) {
                        return 'Selected';
                      }
                      return 'Select Row';
                    }),
                  );
                }),
              );
            },
            readonly: true,
            onClick: (elementId: CanvasElementId, props: FormContainerPropsModel) => {
              if (props?.content?.origin === 'generated') {
                return;
              }

              const dataBindings: CanvasElementDataProperties[] | undefined = props.data;
              const dataSource = dataBindings?.find(
                (b) => b.enabled !== undefined && b.key === 'value',
              )?.dataSource;
              if (!dataSource) {
                return;
              }
              const isActionMode = !!props.content.action;

              inject(Store).dispatch(
                showEditFiltersDialog({
                  elementId,
                  elementType: formVariant,
                  bindingKey: 'value',
                  dataBindings,
                  dataSource,
                  limitResults: isActionMode,
                  mode: 'normal',
                }),
              );
            },
          }),
          'content',
          (content: FormContainerPropsModel['content']) =>
            content?.action !== 'update' || (content?.model?.length || 0) === 0,
        ),
        epc.utils.lockOverride(
          epc.utils.conditionalField(
            epc.utils.disabledField(
              epc.fields.toggleField(`${ELEMENT_PATH_FG_WN}upsert`, `Add row if it doesn't exist`, {
                renderType: 'check',
              }),
              () => 'Action cannot be modified in an auto-generated form.',
            ),
            'content',
            (model: FormContainerPropsModel['content']) =>
              model.origin !== 'form-group' || model.action !== 'update',
          ),
        ),
        epc.utils.lockOverride(
          epc.utils.conditionalField(
            epc.fields.toggleField(
              `${ELEMENT_PATH_WF}nodes.?nodeType:Update.upsert`,
              `Add row if it doesn't exist`,
              { renderType: 'check' },
            ),
            'content',
            (model: FormContainerPropsModel['content']) =>
              model.origin === 'form-group' ||
              model.origin === 'generated' ||
              model.action !== 'update',
          ),
        ),
      ],
      {
        labelWidth: '20%',
        hideWhen: (_, __, model: FormContainerPropsModel) => isGeneratedPrompt(model),
        lockWhen: (model: FormContainerPropsModel) =>
          ['generated', 'form-group', 'wizard'].includes(model.content.origin)
            ? 'Action cannot be modified in an auto-generated form.'
            : undefined,
        hiddenMessage: 'This form is autogenerated, to change the fields go to Action definition.',
      },
    );
  }

  if (formVariant === 'formDetails') {
    return epc.subsections.dataSubsection(
      'data.?key:value.enabled',
      'Data',
      [],
      [
        epc.fields.selectDataSourceField(
          {
            key: 'value',
            label: 'Data Source',
            sources: 'list',
            bindings: [],
          },
          'normal',
          '',
          { hideAiButton: true },
        ),
      ],
      {
        enabledByDefault: true,
        subsectionOptions: {
          lockWhen: (model: FormContainerPropsModel) =>
            ['generated', 'form-group', 'wizard'].includes(model.content.origin)
              ? 'Data source cannot be modified in an auto-generated details view.'
              : undefined,
        },
      },
    );
  }

  throw new Error(`Unexpected form variant: ${formVariant}`);
}

function isGeneratedPrompt(model: FormContainerPropsModel): boolean {
  return model.content.origin === 'generated' && model.content.action === 'prompt';
}
